import React, { useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  TextInput,
  TextareaInput,
  CheckboxField,
  type OptionItem,
} from "@/components/formfields";
import { SelectDropdown } from "@/components/formfields/SelectDropdown";
import type { ExperienceDetail, ExperiencePayload } from "@/interface/profile";
import type { SkillDetail } from "@/interface/masterManagement";
import {
  useCreateExperience,
  useUpdateExperience,
} from "../hooks/useExperience";
import { useInfiniteSkillList } from "@/pages/master-management/hooks/useSkill";
import { useProfileDetail } from "../hooks/useProfile";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const experienceSchema = z
  .object({
    company: z
      .string()
      .trim()
      .max(255, "Company Name cannot exceed 255 characters"),
    role: z
      .string()
      .trim()
      .min(1, "Job Title / Role is required")
      .max(255, "Role cannot exceed 255 characters"),
    location: z
      .string()
      .trim()
      .max(255, "Location cannot exceed 255 characters"),
    start_date: z.string().trim().min(1, "Start Date is required"),
    end_date: z.string().trim().optional(),
    currently_working: z.boolean().default(false),
    description: z.string().trim().optional(),
    skills_used: z.array(z.number()).default([]),
  })
  .superRefine((data, ctx) => {
    const todayStr = getTodayDateString();

    if (data.start_date && data.start_date > todayStr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date cannot be in the future",
        path: ["start_date"],
      });
    }

    if (!data.currently_working) {
      if (!data.end_date || !data.end_date.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End Date is required",
          path: ["end_date"],
        });
      } else {
        if (data.end_date > todayStr) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "End date cannot be in the future",
            path: ["end_date"],
          });
        }

        if (data.start_date && data.end_date) {
          const start = new Date(data.start_date);
          const end = new Date(data.end_date);

          if (
            !isNaN(start.getTime()) &&
            !isNaN(end.getTime()) &&
            start >= end
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Start date must be before End date",
              path: ["end_date"],
            });
          }
        }
      }
    }
  });

export type ExperienceFormValues = z.infer<typeof experienceSchema>;

interface ExperienceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: ExperienceDetail | null;
  profileId?: number | string;
}

export const ExperienceFormModal: React.FC<ExperienceFormModalProps> = ({
  isOpen,
  onClose,
  editData,
  profileId,
}) => {
  const createMutation = useCreateExperience();
  const updateMutation = useUpdateExperience();

  const { data: profileResponse } = useProfileDetail();
  const selfId = profileResponse?.data?.id;
  const targetUserId = profileId ?? selfId ?? editData?.user_profile;

  const [skillSearch, setSkillSearch] = useState<string>("");
  const [debouncedSkillSearch, setDebouncedSkillSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSkillSearch(skillSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [skillSearch]);

  const {
    data: skillData,
    fetchNextPage: fetchNextSkillPage,
    hasNextPage: hasNextSkillPage,
    isFetchingNextPage: isFetchingNextSkillPage,
    isLoading: isSkillsLoading,
  } = useInfiniteSkillList({
    page: 1,
    page_size: 10,
    search: debouncedSkillSearch || undefined,
  });

  const skillsList = useMemo(() => {
    if (!skillData?.pages) return [];
    return skillData.pages.flatMap((page) => {
      const items = Array.isArray(page?.data)
        ? page.data
        : page?.data?.results || [];
      return items;
    });
  }, [skillData]);

  const skillOptions: OptionItem[] = useMemo(() => {
    return skillsList
      .map((s: SkillDetail) => ({
        label: s.skill_name || "",
        value: String(s.id),
      }))
      .filter((opt: OptionItem) => Boolean(opt.label));
  }, [skillsList]);

  // Maintain historical skills map so chip labels are retained across remote searches
  const skillCacheRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    skillsList.forEach((s: SkillDetail) => {
      if (s?.id && s?.skill_name) {
        skillCacheRef.current.set(Number(s.id), s.skill_name);
      }
    });
    if (editData && Array.isArray(editData.skills_used)) {
      editData.skills_used.forEach(
        (s: number | string | { id?: number | string; skill_name?: string }) => {
          if (typeof s === "object" && s?.id && s?.skill_name) {
            skillCacheRef.current.set(Number(s.id), s.skill_name);
          }
        },
      );
    }
  }, [skillsList, editData]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<ExperienceFormValues>({
    resolver: zodResolver(experienceSchema),
    defaultValues: {
      company: "",
      role: "",
      location: "",
      start_date: "",
      end_date: "",
      currently_working: false,
      description: "",
      skills_used: [],
    },
  });

  const isCurrentlyWorking = watch("currently_working");
  const selectedSkillIds = watch("skills_used") || [];

  const handleAddSkill = (skillIdStr: string) => {
    const numId = Number(skillIdStr);
    if (isNaN(numId) || numId === 0) return;
    if (!selectedSkillIds.includes(numId)) {
      const updated = [...selectedSkillIds, numId];
      setValue("skills_used", updated, { shouldValidate: true });
    }
    setSkillSearch("");
    setDebouncedSkillSearch("");
  };

  const handleRemoveSkill = (skillIdToRemove: number) => {
    const updated = selectedSkillIds.filter((id) => id !== skillIdToRemove);
    setValue("skills_used", updated, { shouldValidate: true });
  };

  const getSkillName = (id: number): string => {
    const cached = skillCacheRef.current.get(id);
    if (cached) return cached;
    const found = skillsList.find((s) => Number(s.id) === id);
    if (found) return found.skill_name;
    if (editData && Array.isArray(editData.skills_used)) {
      const item = editData.skills_used.find(
        (s: number | string | { id?: number | string; skill_name?: string }) =>
          typeof s === "object" && Number(s.id) === id,
      );
      if (item && typeof item === "object" && item.skill_name)
        return item.skill_name;
    }
    return `Skill #${id}`;
  };

  useEffect(() => {
    if (isOpen) {
      setSkillSearch("");
      setDebouncedSkillSearch("");
      if (editData) {
        const skillIds: number[] = Array.isArray(editData.skills_used)
          ? editData.skills_used
              .map((s: number | string | { id?: number | string }) =>
                typeof s === "object" ? Number(s.id) : Number(s),
              )
              .filter((n) => !isNaN(n))
          : [];

        reset({
          company: editData.company || "",
          role: editData.role || "",
          location: editData.location || "",
          start_date: editData.start_date || "",
          end_date: editData.end_date || "",
          currently_working: editData.currently_working ?? false,
          description: editData.description || "",
          skills_used: skillIds,
        });
      } else {
        reset({
          company: "",
          role: "",
          location: "",
          start_date: "",
          end_date: "",
          currently_working: false,
          description: "",
          skills_used: [],
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: ExperienceFormValues) => {
    try {
      const payload: ExperiencePayload = {
        company: values.company,
        role: values.role,
        location: values.location,
        start_date: values.start_date,
        end_date: values.currently_working ? null : values.end_date || null,
        currently_working: values.currently_working,
        description: values.description || "",
        user_profile: targetUserId,
        skills_used: values.skills_used,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id + "", payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Handled by query mutation
    }
  };

  const todayStr = useMemo(() => getTodayDateString(), []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {editData ? "Edit Work Experience" : "Add Work Experience"}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="role"
              label="Job Title / Role"
              placeholder="e.g. Senior Full Stack Engineer"
              required
            />
            <TextInput
              control={control}
              name="company"
              label="Company Name"
              placeholder="e.g. DRC Systems India Pvt. Ltd"
            />
            <TextInput
              control={control}
              name="location"
              label="Location"
              placeholder="e.g. San Francisco, CA (Remote)"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="start_date"
                type="date"
                label="Start Date"
                max={todayStr}
                required
              />
              {!isCurrentlyWorking && (
                <TextInput
                  control={control}
                  name="end_date"
                  type="date"
                  label="End Date"
                  max={todayStr}
                  required
                />
              )}
            </div>
            <CheckboxField
              control={control}
              name="currently_working"
              label="I currently work in this role"
            />

            <div className="space-y-2">
              <SelectDropdown
                label="Technologies / Skills Used"
                placeholder={
                  isSkillsLoading ? "Loading skills..." : "Select skill..."
                }
                options={skillOptions}
                onChange={(val) => handleAddSkill(String(val))}
                value=""
                isSearchable
                onSearch={(query) => setSkillSearch(query)}
                onMenuScrollToBottom={() => {
                  if (hasNextSkillPage && !isFetchingNextSkillPage) {
                    fetchNextSkillPage();
                  }
                }}
                hasMore={hasNextSkillPage}
                isLoadingMore={isFetchingNextSkillPage}
              />

              {/* Multi-Select Chips List */}
              {selectedSkillIds.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedSkillIds.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50 transition-all shadow-sm"
                    >
                      {getSkillName(id)}
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(id)}
                        className="text-purple-500 hover:text-purple-700 dark:hover:text-purple-200 p-0.5 rounded-full hover:bg-purple-200/60 dark:hover:bg-purple-800/60 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <TextareaInput
              control={control}
              name="description"
              label="Responsibilities & Achievements"
              placeholder="Describe your contributions and impacts..."
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Experience"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExperienceFormModal;
